import { useLayoutEffect } from 'react'
import { useMatches } from 'react-router-dom'

/**
 * How much of a wide screen a route gets. Phones never see this: below `md`
 * the frame is the whole width whatever the route says (see `.app-frame` in
 * index.scss).
 *
 * - (default)  720px — every app page: a roomier version of the phone column
 *                       it was designed in. Lists stay one column on purpose
 *                       (the funds list is a drag-to-reorder list), and at
 *                       1120px they read as stretched rather than roomy.
 * - `compact`  560px — sign-in and other one-field screens.
 *
 * The admin dashboard is laid out in columns and has its own wider container
 * (AdminDashboardLayout).
 *
 * A route declares it with `handle: { frame: 'compact' }`; the deepest route that
 * says anything wins. It lands on <html> rather than on the layout because the
 * bars that follow the frame (footer, sticky actions, floating buttons) are
 * portaled to <body> and would never inherit it from inside the page.
 */
export type Frame = 'compact'

export interface FrameHandle {
    frame?: Frame
}

export const useRouteFrame = () => {
    const matches = useMatches()
    const frame = matches
        .map((match) => (match.handle as FrameHandle | undefined)?.frame)
        .filter(Boolean)
        .pop()

    useLayoutEffect(() => {
        const root = document.documentElement
        if (frame) root.dataset.frame = frame
        else delete root.dataset.frame
    }, [frame])
}
